// Leitura de uma CONVERSA.
//
// A lista vem do índice; o CORPO vem do IMAP, e só ao abrir. É a decisão de
// desempenho que inviabilizou o Roundcube quando feita ao contrário.
//
// A tela mostra a conversa inteira, não uma mensagem solta. Veio de usar: ao
// abrir uma mensagem recebida não dava para ver a própria resposta sem ir até
// "Enviados" procurar — metade do diálogo ficava escondida.
//
// O corpo de cada mensagem continua vindo uma por vez, ao expandir. Baixar a
// conversa inteira do IMAP seria trinta idas para mostrar uma. As exceções
// são duas: a mensagem clicada e a ÚLTIMA da conversa, que nascem abertas.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as correio from "../api/correio";
import { explicar } from "../api/problema";
import type { CursorDe, Mensagem, MensagemResumo } from "../api/tipos";

/** O formato que `useInfiniteQuery` guarda para a lista de mensagens. */
type InfinitoDeMensagens = { pages: CursorDe<MensagemResumo>[]; pageParams: unknown[] };
import { Icone } from "../ui/Icone";
import { corDoAvatar, dataCurta, dataLonga, inicial, nomeCurto, tamanho } from "../ui/formato";
import { ehEncaminhamento, inicioNoHtml, marcarCitacao, resumoSemCitacao, separarCitacao } from "./citacao";
import { useListaDaPasta } from "./Lista";
import { Etiqueta, MenuDeMarcadores, apenasDoUsuario } from "./Marcadores";
import { vizinhas } from "./navegacao";
import { MenuDePastas } from "./Pastas";

export type ModoDeResposta = "responder" | "responder_todos" | "encaminhar";

export function Leitura({
  aoResponder,
}: {
  aoResponder: (modo: ModoDeResposta, mensagem: Mensagem) => void;
}) {
  const { pasta = "INBOX", uuid = "" } = useParams();
  const navegar = useNavigate();
  const fila = useQueryClient();

  // A LIBERAÇÃO DE IMAGENS É DE UMA MENSAGEM SÓ. Guarda-se PARA QUAL, e não
  // um sim/não: com "Anterior" e "Próximo" esta tela não é recriada ao trocar
  // de mensagem, e um booleano carregaria a liberação para a seguinte —
  // avisando um remetente que a pessoa nunca autorizou de que ela leu.
  //
  // Um CONJUNTO, e não um só: a clicada e a última da conversa ficam abertas
  // juntas, e liberar numa não pode liberar na outra.
  const [liberadas, setLiberadas] = useState<ReadonlySet<string>>(() => new Set());
  const liberar = (id: string) => setLiberadas((antes) => new Set(antes).add(id));
  const imagens = liberadas.has(uuid);

  const consulta = useQuery({
    queryKey: ["mensagem", uuid, imagens],
    queryFn: () => correio.ler(uuid, { carregar_imagens: imagens }),
  });

  // A conversa é resumo, e barata. Falhar aqui NÃO pode esconder a mensagem
  // aberta: se a conversa não vier, a tela cai no comportamento antigo e
  // mostra só ela. Ler o e-mail é o essencial; ver o histórico é o conforto.
  const fio = useQuery({
    queryKey: ["conversa", uuid],
    queryFn: ({ signal }) => correio.conversa(uuid, signal),
  });

  // Os marcadores já em uso na pasta viram sugestão: sem elas, cada mensagem
  // ganharia o mesmo marcador escrito de um jeito diferente.
  //
  // CHAVE PRÓPRIA, e isto é correção de defeito: esta consulta usava
  // ["mensagens", pasta], a MESMA da lista — que é `useInfiniteQuery` e
  // guarda {pages:[…]}. Esta guarda {itens:[…]}. Duas formas no mesmo lugar
  // do cache: quem montasse por último sobrescrevia a outra, e a lista
  // voltava sem `pages`. Chave diferente para dado diferente.
  const daPasta = useQuery({
    queryKey: ["marcadores-da-pasta", pasta],
    queryFn: () => correio.listar({ pasta, limite: 100 }),
  });
  // A ÚLTIMA DA CONVERSA SEMPRE ABERTA, no mesmo formato da clicada. Pedido
  // do Jhou: "pelo menos aparecer sempre o último como se fosse aberto". Ela
  // é LIDA de verdade (marca como lida), porque aparece inteira na tela —
  // como no Gmail, abrir a conversa é ler a conversa.
  //
  // Só quando a clicada está no fio e não é ela mesma a última. Aberta pela
  // Lixeira, a clicada não está no fio (a conversa exclui a Lixeira), e
  // pendurar a última embaixo dela, sem o meio, contaria a história errada.
  const itensDoFio = fio.data?.itens ?? [];
  const posicaoNoFio = itensDoFio.findIndex((i) => i.uuid === uuid);
  const outraUltima =
    posicaoNoFio >= 0 && posicaoNoFio < itensDoFio.length - 1
      ? itensDoFio[itensDoFio.length - 1]
      : undefined;
  const uuidUltima = outraUltima?.uuid ?? "";
  const imagensUltima = liberadas.has(uuidUltima);
  const ultima = useQuery({
    // A MESMA chave da leitura normal: indo depois até ela por "Próximo", o
    // cache já está pronto e ela já está marcada como lida no servidor.
    queryKey: ["mensagem", uuidUltima, imagensUltima],
    queryFn: () => correio.ler(uuidUltima, { carregar_imagens: imagensUltima }),
    enabled: uuidUltima !== "",
  });

  const sugestoes = [
    ...new Set((daPasta.data?.itens ?? []).flatMap((m) => apenasDoUsuario(m.marcadores))),
  ].sort((a, b) => a.localeCompare(b, "pt-BR"));

  // ANTERIOR E PRÓXIMO, na ordem da lista — ver navegacao.ts.
  //
  // É a MESMA consulta da tela de lista (mesma chave, mesmo cache): vindo
  // dela, não há requisição nova. E ela fica montada enquanto se passa de uma
  // mensagem para outra, então clicar em "Próximo" não refaz a lista.
  //
  // NÃO SE BUSCA A PRÓXIMA ANTES DO CLIQUE, de propósito: abrir uma mensagem
  // a marca como lida no servidor, e trazê-la adiantada marcaria como lida
  // uma mensagem que ninguém abriu.
  const lista = useListaDaPasta(pasta);
  const lado = vizinhas(lista.data?.pages.flatMap((p) => p.itens ?? []) ?? [], uuid);
  const pastasDaCaixa = useQuery({ queryKey: ["pastas"], queryFn: () => correio.pastas() });

  // Na última linha carregada, a próxima está na página seguinte da lista —
  // que é consulta ao índice, barata. Sem isto o "Próximo" apagaria a cada
  // 50 mensagens, como se a pasta tivesse acabado.
  const buscarSeguinte = lista.fetchNextPage;
  const precisaDaSeguinte =
    lado.posicao >= 0 && !lado.proxima && lista.hasNextPage && !lista.isFetchingNextPage;
  useEffect(() => {
    if (precisaDaSeguinte) void buscarSeguinte();
  }, [precisaDaSeguinte, buscarSeguinte]);

  const irPara = (alvo?: string) => {
    if (alvo) navegar(`/correio/${encodeURIComponent(pasta)}/${alvo}`);
  };

  // ABRIR A MENSAGEM JÁ A MARCA COMO LIDA — no servidor. A lista que está no
  // cache não fica sabendo, e ao voltar da leitura a mensagem continuava em
  // negrito até alguém clicar em atualizar.
  //
  // O conserto NÃO é buscar mais vezes. O `refetchInterval` da lista passa a
  // cada 10 s, e até lá ela mostraria em negrito o que acabou de ser lido —
  // inclusive ao voltar, quando o react-query vê o dado ainda fresco
  // (`staleTime`) e não busca. Buscar de novo também seria pedir ao servidor
  // algo que já sabemos.
  //
  // Então o cache é CORRIGIDO no lugar, que é instantâneo e não gera
  // requisição. Os contadores das pastas, esses sim, vêm do servidor: o
  // número de não lidas é contado lá.
  const lidaAgora = useCallback(
    (id: string) => {
      fila.setQueriesData<InfinitoDeMensagens>(
        { queryKey: ["mensagens"] },
        (antigo) =>
          antigo?.pages
            ? {
                ...antigo,
                pages: antigo.pages.map((p) => ({
                  ...p,
                  itens: (p.itens ?? []).map((m) => (m.uuid === id ? { ...m, lida: true } : m)),
                })),
              }
            : antigo,
      );
      void fila.invalidateQueries({ queryKey: ["pastas"] });
    },
    [fila],
  );
  useEffect(() => {
    if (consulta.data?.uuid) lidaAgora(uuid);
  }, [consulta.data?.uuid, uuid, lidaAgora]);
  useEffect(() => {
    if (ultima.data?.uuid) lidaAgora(ultima.data.uuid);
  }, [ultima.data?.uuid, lidaAgora]);

  const invalidar = () => {
    void fila.invalidateQueries({ queryKey: ["mensagens"] });
    void fila.invalidateQueries({ queryKey: ["pastas"] });
  };

  const mover = useMutation({
    mutationFn: (destino: string) => correio.mover([uuid], destino),
    onSuccess: () => {
      invalidar();
      navegar(`/correio/${encodeURIComponent(pasta)}`);
    },
  });

  const apagar = useMutation({
    mutationFn: () => correio.apagar(uuid),
    onSuccess: () => {
      invalidar();
      navegar(`/correio/${encodeURIComponent(pasta)}`);
    },
  });

  const voltar = (
    <Link
      to={`/correio/${encodeURIComponent(pasta)}`}
      className="p-2 rounded-full hover:bg-fundo"
      aria-label="Voltar"
      title="Voltar (u)"
    >
      {/* Seta para a esquerda, não a seta circular do mockup: aquela é o
          desenho de "desfazer" e de "atualizar", e ao lado de Anterior e
          Próximo passava por botão de recarregar. */}
      <Icone nome="ArrowLeft" tamanho={18} />
    </Link>
  );

  // Só aparece quando a mensagem está na lista da pasta. Aberta pela busca,
  // "a próxima" não tem sentido — e um botão apagado sem explicação parece
  // defeito.
  //
  // O DESENHO É O DO GMAIL: "3 de 2.630" e duas setas, juntos no canto. A
  // primeira versão tinha "‹ Anterior" e "› Próximo" por extenso, e no canto
  // da barra eles se liam como mais duas ações sobre a mensagem, iguais a
  // Arquivar e Excluir. O contador é o que diz que aquilo é PERCORRER a
  // pasta, e diz onde se está nela.
  //
  // A dica diz a DIREÇÃO, que é a dúvida de verdade: a lista vem da mais
  // nova para a mais antiga, então "Próximo" é a mais antiga.
  //
  // O total é o da barra lateral (mesma consulta, mesmo cache). Ele conta o
  // que o Dovecot tem; a posição conta o índice. Se os dois discordarem, o
  // contador some em vez de dizer "52 de 50".
  const total = pastasDaCaixa.data?.pastas.find((p) => p.nome === pasta)?.total;
  const rotuloDe = (nome?: string) =>
    pastasDaCaixa.data?.pastas.find((p) => p.nome === nome)?.rotulo || nome;
  const navegacao = lado.posicao >= 0 && (
    <div className="ml-auto flex items-center">
      {total !== undefined && total > lado.posicao && (
        <span className="hidden sm:inline px-2 text-sm text-tinta-fraca tabular-nums whitespace-nowrap">
          {(lado.posicao + 1).toLocaleString("pt-BR")} de {total.toLocaleString("pt-BR")}
        </span>
      )}
      <Botao
        icone="ChevronLeft"
        titulo="Anterior (mais recente)"
        desativado={!lado.anterior}
        aoClicar={() => irPara(lado.anterior)}
      />
      <Botao
        icone="ChevronRight"
        titulo="Próximo (mais antiga)"
        desativado={!lado.proxima}
        aoClicar={() => irPara(lado.proxima)}
      />
    </div>
  );

  // Carregando ou com erro, a barra continua com Voltar e a navegação NO
  // MESMO LUGAR. Sem isto, cada "Próximo" tirava os botões da tela enquanto a
  // mensagem chegava, e o segundo clique caía no vazio.
  if (consulta.isPending || consulta.isError) {
    return (
      <div className="flex-1 flex flex-col bg-white rounded-tl-2xl overflow-hidden min-w-0">
        <div className="flex items-center gap-1 px-3 py-2 border-b border-linha-suave text-tinta-media">
          {voltar}
          {navegacao}
        </div>
        <div className="flex-1 grid place-items-center text-sm text-tinta-fraca">
          {consulta.isPending ? "carregando…" : explicar(consulta.error)}
        </div>
      </div>
    );
  }

  const m = consulta.data;

  // A conversa em volta da clicada: o que veio antes fica acima, recolhido; o
  // que veio depois fica abaixo — recolhido, MENOS a última, que aparece
  // inteira (`outraUltima`, lá em cima). É a ordem do diálogo, e é o que põe
  // a RESPOSTA QUE EU DEI logo abaixo da mensagem que a motivou.
  //
  // A CLICADA CONTINUA ABERTA mesmo não sendo a última: Arquivar, Excluir,
  // Spam e Marcadores, na barra, agem sobre ELA — é a linha da lista que a
  // pessoa escolheu. Fechá-la seria pedir para alguém excluir o que não está
  // vendo.
  const naConversa = itensDoFio;
  const antes = posicaoNoFio > 0 ? naConversa.slice(0, posicaoNoFio) : [];
  const depois = posicaoNoFio >= 0 ? naConversa.slice(posicaoNoFio + 1) : [];
  const entre = outraUltima ? depois.slice(0, -1) : depois;

  // RESPONDER, embaixo, responde à ÚLTIMA quando ela está aberta embaixo: é
  // o botão logo abaixo dela, e é continuar a conversa de onde ela está —
  // o `In-Reply-To` aponta para a mais recente, e a citação traz o fio todo.
  const alvo = outraUltima && ultima.data ? ultima.data : m;

  // Esconder a citação só faz sentido quando o que ela cita está NA TELA, ou
  // seja, numa conversa de mais de uma mensagem. Sozinha, a citação é o
  // único contexto que a pessoa tem. E encaminhamento nunca: ali o "citado"
  // é o conteúdo.
  const recolher = (assunto?: string | null) =>
    naConversa.length > 1 && posicaoNoFio >= 0 && !ehEncaminhamento(assunto);

  return (
    <div className="flex-1 flex flex-col bg-white rounded-tl-2xl overflow-hidden min-w-0">
      {/* `flex-wrap`: no celular, com Anterior e Próximo, a barra não cabe numa
          linha, e a navegação desce para a segunda em vez de empurrar os
          botões para fora da tela. Rolagem horizontal cortaria os menus de
          pasta e de marcador, que abrem para baixo desta barra. */}
      <div className="flex flex-wrap items-center gap-1 px-3 py-2 border-b border-linha-suave text-tinta-media">
        {voltar}
        <Botao icone="Archive" titulo="Arquivar (e)" rotulo="Arquivar" aoClicar={() => mover.mutate("Archive")} />
        {/* "Marcar como spam" só faz sentido no que você RECEBEU. Em Enviados
            e Rascunhos a mensagem é sua, e denunciar a si mesmo ao antispam
            ensina o filtro errado: o aprendizado é por caixa, e marcaria o
            próprio remetente como spam. */}
        {pasta !== "Sent" && pasta !== "Drafts" && (
          <Botao icone="ShieldAlert" titulo="Marcar como spam" rotulo="Spam" aoClicar={() => mover.mutate("Junk")} />
        )}
        <Botao icone="Trash2" titulo="Excluir (#)" rotulo="Excluir" aoClicar={() => apagar.mutate()} />
        <MenuDePastas atual={pasta} aoEscolher={(n) => mover.mutate(n)} />
        <MenuDeMarcadores uuid={uuid} atuais={m.marcadores ?? []} sugestoes={sugestoes} />
        <Botao
          icone="MailOpen"
          titulo="Marcar como não lida"
          aoClicar={() => {
            void correio.marcar(uuid, { lida: false }).then(() => {
              invalidar();
              navegar(`/correio/${encodeURIComponent(pasta)}`);
            });
          }}
        />
        {navegacao}
      </div>

      {/* `key`: cada mensagem começa do TOPO. Passando de uma longa para a
          seguinte sem isto, a rolagem ficava onde a anterior terminou. */}
      <div key={uuid} className="px-3 sm:px-6 py-4 overflow-y-auto">
        <h1 className="text-lg sm:text-xl text-tinta mb-2 break-words">
          {m.assunto || "(sem assunto)"}
          {naConversa.length > 1 && (
            <span className="ml-2 align-middle text-xs text-tinta-fraca whitespace-nowrap">
              {naConversa.length} mensagens
            </span>
          )}
        </h1>
        {apenasDoUsuario(m.marcadores).length > 0 && (
          <p className="mb-3">
            {apenasDoUsuario(m.marcadores).map((r) => (
              <Etiqueta key={r} nome={r} />
            ))}
          </p>
        )}

        {/* As anteriores ficam RECOLHIDAS, acima. Abertas de uma vez, uma
            conversa longa empurraria a mensagem que a pessoa veio ler para
            fora da tela — que é o oposto do que o recurso serve. */}
        {antes.map((a) => (
          <Recolhida key={a.uuid} resumo={a} pasta={pasta} rotuloDaPasta={rotuloDe(a.pasta)} />
        ))}

        <Cartao
          m={m}
          recolherCitacao={recolher(m.assunto)}
          imagensLiberadas={imagens}
          aoLiberarImagens={() => liberar(uuid)}
        />

        {entre.map((d) => (
          <Recolhida key={d.uuid} resumo={d} pasta={pasta} rotuloDaPasta={rotuloDe(d.pasta)} />
        ))}

        {/* A última. Enquanto o corpo dela chega, fica a linha recolhida no
            lugar (sem pular a tela); se a leitura falhar, a linha continua
            lá e abre ao toque, pelo caminho da "espiada". */}
        {outraUltima &&
          (ultima.data ? (
            <Cartao
              m={ultima.data}
              recolherCitacao={recolher(ultima.data.assunto)}
              imagensLiberadas={imagensUltima}
              aoLiberarImagens={() => liberar(uuidUltima)}
            />
          ) : (
            <Recolhida
              key={uuidUltima}
              resumo={outraUltima}
              pasta={pasta}
              rotuloDaPasta={rotuloDe(outraUltima.pasta)}
            />
          ))}

        <div className="flex flex-wrap gap-2 mt-3">
          <Acao icone="Reply" rotulo="Responder" aoClicar={() => aoResponder("responder", alvo)} />
          {/* "Responder a todos" só aparece quando há a quem: numa mensagem
              com um destinatário só, o botão faria exatamente o mesmo que o
              anterior e só teria a função de confundir. */}
          {(alvo.destinatarios?.length ?? 0) > 1 && (
            <Acao
              icone="ReplyAll"
              rotulo="Responder a todos"
              aoClicar={() => aoResponder("responder_todos", alvo)}
            />
          )}
          <Acao icone="Forward" rotulo="Encaminhar" aoClicar={() => aoResponder("encaminhar", alvo)} />
        </div>
      </div>
    </div>
  );
}

/**
 * Uma mensagem INTEIRA da conversa: cabeçalho, corpo, imagens, anexos.
 *
 * É o formato da clicada, e da última quando ela é outra — o "como se fosse
 * aberto" do pedido. Um componente só para as duas, para que nunca pareçam
 * coisas diferentes.
 */
function Cartao({
  m,
  recolherCitacao,
  imagensLiberadas,
  aoLiberarImagens,
}: {
  m: Mensagem;
  recolherCitacao: boolean;
  imagensLiberadas: boolean;
  aoLiberarImagens: () => void;
}) {
  const remetente = m.remetente ?? "";
  const anexos = (m.anexos ?? []).filter((a) => !a.embutido);
  return (
    <article className="mb-2 rounded-lg border border-linha">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 sm:px-4 py-3">
        <span
          className="h-9 w-9 rounded-full grid place-items-center text-white text-sm flex-shrink-0"
          style={{ background: corDoAvatar(remetente) }}
          aria-hidden="true"
        >
          {inicial(m.remetente_nome, remetente)}
        </span>
        <div className="flex-1 min-w-0 text-sm">
          <span className="font-semibold">{nomeCurto(m.remetente_nome, remetente)}</span>{" "}
          <span className="text-tinta-fraca">&lt;{remetente}&gt;</span>
          <div className="text-xs text-tinta-fraca truncate">
            para {(m.destinatarios ?? []).join(", ")}
          </div>
        </div>
        <span className="text-xs text-tinta-fraca whitespace-nowrap">{dataLonga(m.data)}</span>
      </header>

      <Autenticacao dados={m.autenticacao} />

      <Corpo
        html={m.corpo_html}
        texto={m.corpo_texto}
        recolherCitacao={recolherCitacao}
        className="px-4 pb-4"
      />

      {!!m.imagens_bloqueadas && !imagensLiberadas && (
        <div className="mx-4 mb-4 flex items-center gap-3 rounded-lg border border-aviso/40 bg-aviso/10 px-3 py-2 text-xs text-tinta">
          <Icone nome="Info" tamanho={16} className="text-aviso flex-shrink-0" />
          <span className="flex-1">
            {m.imagens_bloqueadas} imagem(ns) bloqueada(s). Carregá-las avisa quem enviou
            que você leu a mensagem, de onde e quando.
          </span>
          <button onClick={aoLiberarImagens} className="text-acao underline whitespace-nowrap">
            Carregar mesmo assim
          </button>
        </div>
      )}

      {anexos.length > 0 && (
        <div className="mx-4 mb-4 flex flex-wrap gap-3">
          {anexos.map((a) => (
            <Anexo key={a.parte} uuid={m.uuid ?? ""} anexo={a} />
          ))}
        </div>
      )}
    </article>
  );
}

/**
 * O corpo de uma mensagem, com o texto CITADO recolhido atrás de "•••"
 * (ver citacao.ts). Usado pelo cartão e pela linha recolhida ao expandir.
 *
 * O HTML já vem HIGIENIZADO do servidor (bluemonday). Não se higieniza de
 * novo aqui: duas higienizações divergem com o tempo, e a do navegador seria
 * a que ninguém testa. Pelo mesmo motivo a citação não é CORTADA do HTML —
 * os nós dela são marcados no DOM já desenhado, e o CSS os esconde.
 *
 * `[&_img[data-src-bloqueado]]:hidden` ESCONDE a imagem retida. O servidor
 * já tirou o `src` dela, então o navegador não pede nada — mas um `<img>` sem
 * src e com `alt` ainda desenha o texto alternativo solto no meio do e-mail,
 * e "icone-calendario" no corpo da mensagem não ajuda ninguém. Quem explica o
 * que houve é o aviso do cartão, que diz quantas foram e oferece carregar.
 */
function Corpo({
  html,
  texto,
  recolherCitacao,
  className = "",
}: {
  html?: string | null;
  texto?: string | null;
  recolherCitacao: boolean;
  className?: string;
}) {
  const [verCitacao, setVerCitacao] = useState(false);
  const [citacaoNoHtml, setCitacaoNoHtml] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);

  // Antes de pintar, para a citação não aparecer e sumir num piscar. O React
  // não reescreve o `innerHTML` enquanto a string não muda, então as marcas
  // sobrevivem aos redesenhos; quando ela muda (imagens liberadas), o HTML
  // chega novo e é marcado de novo.
  useLayoutEffect(() => {
    const el = raiz.current;
    if (!el || !html || !recolherCitacao) {
      setCitacaoNoHtml(false);
      return;
    }
    if (el.querySelector("[data-citacao]")) {
      setCitacaoNoHtml(true);
      return;
    }
    const inicio = inicioNoHtml(el);
    if (inicio) marcarCitacao(el, inicio);
    setCitacaoNoHtml(inicio !== null);
  }, [html, recolherCitacao]);

  const separado = !html && recolherCitacao ? separarCitacao(texto) : { principal: texto ?? "", citacao: "" };
  const temCitacao = html ? citacaoNoHtml : separado.citacao !== "";
  const escondida = temCitacao && !verCitacao;

  return (
    <div className={className}>
      {html ? (
        <div
          ref={raiz}
          className={`text-sm text-tinta-media leading-relaxed break-words [&_a]:text-acao [&_a]:underline [&_img]:max-w-full [&_img[data-src-bloqueado]]:hidden ${escondida ? "citacao-oculta" : ""}`}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <pre className="text-sm text-tinta-media leading-relaxed whitespace-pre-wrap break-words font-sans">
          {escondida ? separado.principal : texto}
        </pre>
      )}
      {temCitacao && (
        <button
          onClick={() => setVerCitacao((v) => !v)}
          aria-expanded={verCitacao}
          aria-label={verCitacao ? "Esconder o texto citado" : "Mostrar o texto citado"}
          title={verCitacao ? "Esconder o texto citado" : "Mostrar o texto citado"}
          className="mt-2 inline-flex h-5 items-center rounded-full bg-linha px-2 text-xs leading-none tracking-widest text-tinta-media hover:bg-tinta-fraca/25"
        >
          •••
        </button>
      )}
    </div>
  );
}

/**
 * Uma mensagem da conversa que NÃO é a aberta.
 *
 * Nasce recolhida, numa linha só, como no Gmail. O corpo só é buscado quando
 * alguém clica: a conversa vem do índice, e trazer trinta corpos do IMAP para
 * mostrar um seria pagar trinta vezes pelo que não se vai ler.
 */
function Recolhida({
  resumo,
  pasta,
  rotuloDaPasta,
}: {
  resumo: MensagemResumo;
  pasta: string;
  rotuloDaPasta?: string;
}) {
  const [aberta, setAberta] = useState(false);
  const remetente = resumo.remetente ?? "";

  const corpo = useQuery({
    // CHAVE PRÓPRIA — "espiada" não é "lida".
    //
    // Com a chave da leitura normal, abrir esta mensagem depois encontraria
    // o cache pronto, não chamaria o servidor, e portanto NÃO a marcaria como
    // lida lá. A tela diria "lida" e o servidor diria o contrário, até o
    // próximo recarregamento desfazer — o pior tipo de defeito deste produto,
    // porque parece ter funcionado.
    queryKey: ["mensagem-espiada", resumo.uuid],
    // `marcar_lida: false`: expandir para conferir o que já se respondeu não
    // é o mesmo gesto que ler algo novo, e marcar lida aqui faria mensagens
    // sumirem do contador sem ninguém ter aberto de fato.
    queryFn: () => correio.ler(resumo.uuid!, { marcar_lida: false }),
    enabled: aberta,
  });

  return (
    <article className="mb-2 rounded-lg border border-linha bg-fundo/40">
      <button
        onClick={() => setAberta((a) => !a)}
        aria-expanded={aberta}
        className="flex w-full items-center gap-3 px-3 sm:px-4 py-2.5 text-left hover:bg-fundo rounded-lg"
      >
        <span
          className="h-7 w-7 flex-shrink-0 rounded-full grid place-items-center text-white text-xs"
          style={{ background: corDoAvatar(remetente) }}
          aria-hidden="true"
        >
          {inicial(resumo.remetente_nome, remetente)}
        </span>
        {/* Duas linhas no celular, uma no desktop — mesma decisão da lista.
            Testado na tela: sem o trecho, a linha recolhida ficava só com o
            nome e a hora, e não dava para saber se valia abrir.

            O trecho PASSAVA POR CIMA da pasta e da data (visto pelo Jhou).
            Era `sm:inline`: `truncate` é `overflow: hidden`, e overflow não
            corta elemento inline — o texto seguia reto até o fim da linha,
            e ainda criava a rolagem horizontal da leitura. No desktop agora
            é uma linha FLEX: o nome não encolhe, o trecho encolhe e corta
            com reticências, pasta e data têm largura garantida. */}
        <span className="min-w-0 flex-1 text-sm sm:flex sm:items-baseline sm:gap-2">
          <span className="block truncate font-medium sm:max-w-[40%] sm:shrink-0">
            {nomeCurto(resumo.remetente_nome, remetente)}
          </span>
          {!aberta && (
            <span className="block min-w-0 truncate text-xs text-tinta-fraca sm:text-sm">
              {resumoSemCitacao(resumo.preview)}
            </span>
          )}
        </span>
        {resumo.pasta && resumo.pasta !== pasta && (
          <span className="hidden sm:inline shrink-0 rounded bg-linha px-1.5 py-0.5 text-[11px] text-tinta-media whitespace-nowrap">
            {rotuloDaPasta ?? resumo.pasta}
          </span>
        )}
        <span className="shrink-0 text-xs text-tinta-fraca whitespace-nowrap">{dataCurta(resumo.data)}</span>
      </button>

      {aberta && (
        <div className="border-t border-linha px-3 sm:px-4 py-3">
          <p className="mb-2 text-xs text-tinta-fraca break-words">
            para {(resumo.destinatarios ?? []).join(", ")} · {dataLonga(resumo.data)}
          </p>
          {corpo.isPending && <p className="text-sm text-tinta-fraca">carregando…</p>}
          {corpo.isError && <p className="text-sm text-perigo">{explicar(corpo.error)}</p>}
          {corpo.data && (
            <Corpo
              html={corpo.data.corpo_html}
              texto={corpo.data.corpo_texto}
              recolherCitacao={!ehEncaminhamento(resumo.assunto)}
            />
          )}
        </div>
      )}
    </article>
  );
}

function Autenticacao({ dados }: { dados?: { spf?: string; dkim?: string; dmarc?: string } }) {
  if (!dados) return null;
  const falhou = [dados.spf, dados.dkim, dados.dmarc].some((v) => v === "fail");
  if (!falhou) return null;
  // Só aparece quando FALHA. Um selo verde em toda mensagem legítima ensina o
  // usuário a ignorar o selo — e aí o aviso que importa passa despercebido.
  return (
    <div className="mx-4 mb-3 flex items-center gap-2 rounded-lg border border-perigo/40 bg-perigo/10 px-3 py-2 text-xs text-tinta">
      <Icone nome="AlertTriangle" tamanho={16} className="text-perigo flex-shrink-0" />
      <span>
        O remetente não pôde ser confirmado (SPF {dados.spf}, DKIM {dados.dkim}, DMARC{" "}
        {dados.dmarc}). Desconfie de pedidos de dinheiro ou de senha.
      </span>
    </div>
  );
}

/**
 * Nem toda parte tem nome de arquivo.
 *
 * As de uma DEVOLUÇÃO não têm: o relatório de entrega e a mensagem original
 * são partes MIME sem `filename`. O cartão ficava com um vazio onde deveria
 * estar o nome, parecendo anexo corrompido.
 */
function nomeDoAnexo(nome: string | undefined, tipo: string | undefined): string {
  if (nome?.trim()) return nome.trim();
  switch (tipo) {
    case "message/delivery-status":
      return "Relatório de entrega";
    case "message/rfc822":
      return "Mensagem original";
    case "text/plain":
      return "Texto sem nome";
    default:
      return tipo ? `Arquivo (${tipo})` : "Arquivo sem nome";
  }
}

function Anexo({
  uuid,
  anexo,
}: {
  uuid: string;
  anexo: { parte?: string; nome?: string; tipo_declarado?: string; tamanho_bytes?: number };
}) {
  const [baixando, setBaixando] = useState(false);
  const rotulo = nomeDoAnexo(anexo.nome, anexo.tipo_declarado);

  async function baixar() {
    setBaixando(true);
    try {
      const blob = await correio.baixarAnexo(uuid, anexo.parte!);
      // O servidor manda tudo como octet-stream de propósito: servir com o
      // tipo declarado deixaria HTML hostil executar no domínio do webmail.
      // Quem dá nome ao arquivo é esta linha, não o Content-Type.
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // O nome do ARQUIVO salvo não pode ser o rótulo amigável: "Relatório de
      // entrega" viraria um arquivo sem extensão que o sistema não abre.
      a.download = anexo.nome || `${anexo.parte || "anexo"}.eml`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setBaixando(false);
    }
  }

  return (
    <button
      onClick={() => void baixar()}
      disabled={baixando}
      // O NOME DIZ O ARQUIVO. Sem isto o leitor de tela anuncia "botão", e
      // numa mensagem com três anexos a pessoa não tem como escolher qual
      // baixar — o nome está na tela, mas não no nome acessível.
      aria-label={`Baixar ${rotulo}`}
      className="flex items-center gap-3 rounded-lg border border-linha p-3 w-72 text-sm text-left hover:bg-fundo disabled:opacity-60"
    >
      <Icone nome="FileText" tamanho={28} className="text-perigo flex-shrink-0" />
      <span className="flex-1 min-w-0">
        <span className="block font-medium truncate">{rotulo}</span>
        <span className="block text-xs text-tinta-fraca">
          {tamanho(anexo.tamanho_bytes)} · verificado pelo antivírus
        </span>
      </span>
      <Icone nome="Download" tamanho={16} />
    </button>
  );
}

/**
 * Ação da barra da mensagem.
 *
 * O RÓTULO APARECE QUANDO HÁ ESPAÇO. A barra era só ícones — arquivar, spam,
 * excluir, pasta, marcador e não lida —, e seis desenhos em sequência não se
 * leem: a pessoa descobre o que cada um faz clicando, e "marcar como spam" e
 * "excluir" são caros de descobrir errando.
 *
 * O `aria-label` continua sempre, inclusive com o rótulo visível: ele é o
 * texto do `title` completo, com o atalho de teclado, que o rótulo curto não
 * mostra.
 */
function Botao({
  icone,
  titulo,
  rotulo,
  desativado = false,
  aoClicar,
}: {
  icone: Parameters<typeof Icone>[0]["nome"];
  titulo: string;
  rotulo?: string;
  desativado?: boolean;
  aoClicar: () => void;
}) {
  return (
    <button
      onClick={aoClicar}
      disabled={desativado}
      className="flex items-center gap-1.5 rounded-full p-2 hover:bg-fundo disabled:opacity-40 disabled:hover:bg-transparent"
      title={titulo}
      aria-label={titulo}
    >
      <Icone nome={icone} tamanho={18} />
      {rotulo && <span className="hidden text-sm lg:inline">{rotulo}</span>}
    </button>
  );
}

function Acao({ icone, rotulo, aoClicar }: { icone: Parameters<typeof Icone>[0]["nome"]; rotulo: string; aoClicar: () => void }) {
  return (
    <button
      onClick={aoClicar}
      className="flex items-center gap-2 rounded-full border border-linha px-4 py-2 text-sm hover:bg-fundo"
    >
      <Icone nome={icone} tamanho={16} /> {rotulo}
    </button>
  );
}
